import React from 'react';
import { Link } from 'react-router-dom';
import {
  Home, Info, BookOpen, Users, Newspaper, HeartHandshake, Sparkles,
  MapPin, ScrollText, Building2, Camera, Megaphone, Mail, Landmark,
  FileText, Shield, ArrowUpRight,
} from 'lucide-react';

const GROUPS = [
  {
    heading: 'Explore',
    accent: 'from-blue-500 to-cyan-500',
    Icon: Home,
    links: [
      { label: 'Home', to: '/', Icon: Home },
      { label: 'About EWRF', to: '/#about', Icon: Info },
      { label: 'Our Programmes', to: '/#programmes', Icon: BookOpen },
      { label: 'Branches', to: '/branches', Icon: MapPin },
      { label: 'Contact', to: '/#contact', Icon: Mail },
    ],
  },
  {
    heading: 'Get Involved',
    accent: 'from-amber-500 to-orange-500',
    Icon: HeartHandshake,
    links: [
      { label: 'Donate', to: '/#donate', Icon: HeartHandshake },
      { label: 'Volunteer', to: '/#contact', Icon: Sparkles },
      { label: 'Book Counselling', to: '/#contact', Icon: HeartHandshake },
      { label: 'Room Booking', to: '/room-booking', Icon: Landmark },
      { label: 'Careers', to: '/#careers', Icon: Users },
    ],
  },
  {
    heading: 'News & Events',
    accent: 'from-rose-500 to-pink-500',
    Icon: Newspaper,
    links: [
      { label: 'All News', to: '/news', Icon: Newspaper },
      { label: 'HQ Events', to: '/news/hq-events', Icon: Building2 },
      { label: 'Branch Activities', to: '/news/branch-activities', Icon: MapPin },
      { label: 'Media Coverage', to: '/news/media-coverage', Icon: Camera },
      { label: 'News Releases', to: '/news/media-statement', Icon: Megaphone },
      { label: 'Newsletter', to: '/news/newsletter', Icon: Mail },
    ],
  },
  {
    heading: 'Programmes',
    accent: 'from-emerald-500 to-teal-500',
    Icon: BookOpen,
    links: [
      { label: 'Education', to: '/projects/education', Icon: BookOpen },
      { label: 'Counselling', to: '/projects/counselling', Icon: HeartHandshake },
      { label: 'Research', to: '/projects/research', Icon: ScrollText },
      { label: 'Welfare', to: '/projects/welfare', Icon: HeartHandshake },
      { label: 'Volunteers', to: '/projects/volunteers', Icon: Sparkles },
    ],
  },
  {
    heading: 'Legal',
    accent: 'from-slate-500 to-gray-600',
    Icon: Shield,
    links: [
      { label: 'Privacy Policy', to: '/privacy', Icon: Shield },
      { label: 'Terms & Conditions', to: '/terms', Icon: FileText },
      { label: 'Tax-Exempt Info', to: 'https://www.hasil.gov.my/en/individual/individual-life-cycle/how-to-declare-income/tax-reliefs/list-of-approved-institutions/', external: true, Icon: Landmark },
    ],
  },
];

const isHash = (u) => u && u.includes('#');
const isExternal = (u) => u && /^https?:\/\//.test(u);

const Sitemap = () => (
  <section
    id="sitemap"
    className="relative py-16 md:py-20 overflow-hidden bg-gradient-to-b from-white via-[#F7F3EA] to-[#EDE5D3]"
    data-testid="sitemap-section"
  >
    {/* Decorative blobs */}
    <div className="absolute -top-24 -left-24 w-72 h-72 bg-amber-300/25 rounded-full blur-3xl pointer-events-none"></div>
    <div className="absolute -bottom-32 -right-16 w-96 h-96 bg-blue-400/15 rounded-full blur-3xl pointer-events-none"></div>

    <div className="relative max-w-7xl mx-auto px-4">
      <div className="text-center max-w-2xl mx-auto mb-12 md:mb-14">
        <div className="inline-flex items-center gap-2 mb-3">
          <span className="h-[2px] w-8 bg-gradient-to-r from-amber-400 to-orange-500 rounded-full" />
          <span className="text-[11px] uppercase tracking-[0.25em] text-amber-700 font-bold">Everything at a Glance</span>
          <span className="h-[2px] w-8 bg-gradient-to-r from-orange-500 to-amber-400 rounded-full" />
        </div>
        <h2 className="text-3xl md:text-5xl font-serif text-[#0A1628] leading-[1.05]">
          Site{' '}
          <span className="italic bg-gradient-to-r from-amber-500 via-orange-500 to-amber-500 bg-clip-text text-transparent">
            Map
          </span>
        </h2>
        <p className="mt-3 text-sm md:text-base text-gray-600">
          Every page and section of EWRF Malaysia in one place &mdash; jump anywhere in a single click.
        </p>
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
        {GROUPS.map(({ heading, links, accent, Icon }) => (
          <div
            key={heading}
            className="group relative rounded-3xl bg-white/85 backdrop-blur-md border border-white shadow-lg hover:shadow-2xl transition-shadow p-6 overflow-hidden"
            data-testid={`sitemap-group-${heading.replace(/\W+/g,'-').toLowerCase()}`}
          >
            <div className={`absolute inset-x-0 top-0 h-1 bg-gradient-to-r ${accent}`} />
            <div className="flex items-center gap-3 mb-4">
              <div className={`w-10 h-10 rounded-xl bg-gradient-to-br ${accent} text-white flex items-center justify-center shadow-md shrink-0`}>
                <Icon className="w-5 h-5" />
              </div>
              <h3 className="text-lg font-bold text-[#0A1628] tracking-tight">{heading}</h3>
            </div>
            <ul className="space-y-1.5">
              {links.map((l) => {
                const LinkIcon = l.Icon;
                const inner = (
                  <span className="flex items-center gap-2 text-sm text-gray-700 group-hover:text-gray-900 hover:text-[#1565C0] transition-colors py-1.5 pl-1 pr-2 rounded-md hover:bg-[#1565C0]/5">
                    <LinkIcon className="w-3.5 h-3.5 text-gray-400" />
                    <span className="flex-1 truncate">{l.label}</span>
                    <ArrowUpRight className="w-3.5 h-3.5 opacity-0 -translate-x-1 group-hover:opacity-70 group-hover:translate-x-0 transition-all" />
                  </span>
                );
                if (l.external || isExternal(l.to)) {
                  return (
                    <li key={l.label}>
                      <a href={l.to} target="_blank" rel="noreferrer noopener" data-testid={`sitemap-link-${l.label.replace(/\W+/g,'-').toLowerCase()}`}>
                        {inner}
                      </a>
                    </li>
                  );
                }
                if (isHash(l.to)) {
                  return (
                    <li key={l.label}>
                      <a href={l.to} data-testid={`sitemap-link-${l.label.replace(/\W+/g,'-').toLowerCase()}`}>{inner}</a>
                    </li>
                  );
                }
                return (
                  <li key={l.label}>
                    <Link to={l.to} data-testid={`sitemap-link-${l.label.replace(/\W+/g,'-').toLowerCase()}`}>{inner}</Link>
                  </li>
                );
              })}
            </ul>
          </div>
        ))}
      </div>
    </div>
  </section>
);

export default Sitemap;
